import { useEffect, useRef, useState } from 'react';

import type {
  GreenCoffeeFields,
  GreenCoffeeJudge,
  GreenCoffeeListItem,
} from '@/hooks/useGreenCoffee';
import { useImageFallback } from '@/hooks/useImageFallback';
import { countryName } from '@/lib/countries';
import { relTime } from '@/lib/crawlRuns';

import { GreenCoffeeCorrectForm } from './GreenCoffeeCorrectForm';
import {
  AlertIcon,
  BackIcon,
  CheckIcon,
  CircleCheckIcon,
  CrossIcon,
  PencilIcon,
  SproutIcon,
} from './greenCoffeeIcons';
import { KindBadge, StatusPill } from './GreenCoffeeQueueRow';
import {
  changedFieldKeys,
  correctedNote,
  fieldLabels,
  isCorrected,
  FIELD_LABELS,
  isJudgePublished,
  normalizeFields,
  ruleName,
} from './greenCoffeeStatus';

// Header, columns and action bar share one centred measure so they line up and
// the source text stops running the full width of a desktop monitor.
const SHELL = 'mx-auto w-full max-w-[1280px]';

function CorrectedTag() {
  return (
    <span className="ml-1 inline-flex items-center rounded-[5px] bg-wolisho-brown/[0.09] px-1.5 py-px align-[1px] text-[10.5px] font-bold text-wolisho-brown">
      Corrected
    </span>
  );
}

function FieldRow({
  label,
  value,
  corrected = false,
}: {
  label: string;
  value: string | string[] | null;
  corrected?: boolean;
}) {
  const text = Array.isArray(value) ? value.join(', ') : value;
  const empty = text == null || text === '';
  return (
    <div className="flex gap-2.5 border-b border-wolisho-brown/[0.06] py-[7px]">
      <span
        className={`flex-[0_0_92px] text-[11.5px] font-bold ${
          corrected ? 'text-wolisho-brown' : 'text-wolisho-brown/70'
        }`}
      >
        {label}
      </span>
      <span
        className={
          empty
            ? 'flex-1 text-[13.5px] font-bold leading-snug text-black/30'
            : 'flex-1 text-[13.5px] leading-snug text-black'
        }
      >
        {empty ? '—' : text}
        {corrected && <CorrectedTag />}
      </span>
    </div>
  );
}

function SourceLine({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="mb-3 last:mb-0">
      <p className="mb-[3px] text-[10.5px] font-bold uppercase tracking-wider text-wolisho-brown/40">
        {label}
      </p>
      <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-black/[0.78]">
        {value == null || value === '' ? '—' : value}
      </p>
    </div>
  );
}

function SectionHeading({
  label,
  icon,
  trailing,
}: {
  label: string;
  icon?: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="mb-2.5 flex items-center gap-[7px]">
      {icon}
      <h2 className="text-xs font-bold uppercase tracking-wider text-wolisho-brown">{label}</h2>
      <span className="h-px flex-1 bg-wolisho-brown/10" />
      {trailing}
    </div>
  );
}

// The judge's verdict and reason, as the judge wrote them. Shown on every row
// it judged: on a FAIL so the admin sees why it came back, on a PASS as the
// record of why it went live. Never an Agree/Disagree prompt.
function JudgeVerdict({ judge }: { judge: GreenCoffeeJudge }) {
  const fail = judge.verdict === 'FAIL';
  const name = fail ? ruleName(judge.rule) : null;
  const title = fail ? `FAIL · rule ${String(judge.rule)}${name ? ` · ${name}` : ''}` : 'PASS';
  return (
    <section aria-label="Judge verdict" data-testid="green-coffee-judge-verdict">
      <div className="mb-2.5 flex items-center gap-[7px]">
        <span className={`flex ${fail ? 'text-wolisho-red' : 'text-green-700'}`}>
          {fail ? <AlertIcon /> : <CircleCheckIcon />}
        </span>
        <h2 className="text-xs font-bold uppercase tracking-wider text-wolisho-brown">
          Judge verdict
        </h2>
        <span className="h-px flex-1 bg-wolisho-brown/10" />
        <span className="text-[11.5px] font-bold text-wolisho-brown/60">
          {relTime(judge.judgedAt)}
        </span>
      </div>
      <div
        className={`rounded-xl px-4 pb-3.5 pt-3 ${fail ? 'bg-wolisho-red/[0.06]' : 'bg-green-600/[0.07]'}`}
      >
        <p
          className={`mb-1.5 flex items-center gap-1.5 text-[13.5px] font-bold ${
            fail ? 'text-wolisho-red' : 'text-green-700'
          }`}
          data-testid="green-coffee-judge-verdict-title"
        >
          {fail ? <CrossIcon size={13} /> : <CheckIcon size={13} />}
          {title}
        </p>
        <p className="text-[13.5px] leading-relaxed text-black/[0.78]">{judge.reason}</p>
      </div>
    </section>
  );
}

function PublishButton({
  isUpdate,
  disabled,
  onClick,
  testId,
  compact = false,
}: {
  isUpdate: boolean;
  disabled: boolean;
  onClick: () => void;
  testId: string;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg border-0 bg-wolisho-red px-3 font-bold text-white disabled:opacity-60 ${
        compact ? 'flex-[1.4] py-2.5 text-[13.5px]' : 'flex-[1.7] py-3 text-sm'
      }`}
    >
      <CheckIcon size={compact ? 14 : 16} />
      Good — publish{isUpdate && ' update'}
    </button>
  );
}

interface Props {
  green: GreenCoffeeListItem;
  roasterName: string | null;
  position: number;
  total: number;
  readOnly: boolean;
  isSaving: boolean;
  onGood: (id: string, notes?: string) => void;
  onBad: (id: string, notes: string) => void;
  onCorrect: (id: string, fields: GreenCoffeeFields) => void;
  onClose: () => void;
}

type NoteMode = 'good' | 'bad' | null;

function pluralFields(count: number): string {
  return `${String(count)} field${count === 1 ? '' : 's'} changed`;
}

export function GreenCoffeeReviewCard({
  green,
  roasterName,
  position,
  total,
  readOnly,
  isSaving,
  onGood,
  onBad,
  onCorrect,
  onClose,
}: Props) {
  const [noteMode, setNoteMode] = useState<NoteMode>(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    setNoteMode(null);
    setNote('');
  }, [green.id]);

  // Keyed by row id, so the next row in the queue opens in view mode.
  const [draft, setDraft] = useState<{ id: string; fields: GreenCoffeeFields } | null>(null);
  const editing = draft?.id === green.id ? draft.fields : null;
  const corrected = editing ? normalizeFields(editing) : null;
  const changed = corrected ? changedFieldKeys(normalizeFields(green.fields), corrected) : [];
  const exitEdit = () => setDraft(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = editing ? exitEdit : onClose;

  // Modal focus handling: remember the trigger, move focus in, keep Tab inside
  // the dialog (aria-modal promises it) and restore focus on close.
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [
        ...dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])',
        ),
      ];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (!dialogRef.current.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      trigger?.focus();
    };
  }, []);

  const fields = green.fields;
  const showActions = !readOnly;
  const judgePublished = isJudgePublished(green);
  const canPublish = fields.countryCode !== null;
  const correctedKeys = isCorrected(green) ? (green.correctedFields ?? []) : [];
  const reviewerNote = correctedNote(green) ?? green.notes;
  const correctionBlocker = !corrected
    ? null
    : corrected.name === ''
      ? 'Name can’t be empty'
      : corrected.countryCode === null
        ? 'Pick a country to publish'
        : null;
  const { hasError: imageHasError, onError: onImageError } = useImageFallback(green.imageUrl);
  const showImage = green.imageUrl !== null && !imageHasError;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={fields.name}
      tabIndex={-1}
      data-testid="green-coffee-review-card"
      className="fixed inset-0 z-50 flex flex-col bg-wolisho-cream outline-hidden"
    >
      <div className="shrink-0 border-b border-wolisho-brown/[0.08] px-4 py-3.5">
        <div className={`flex items-center gap-2.5 ${SHELL}`}>
          <button
            type="button"
            onClick={editing ? exitEdit : onClose}
            aria-label="Back to queue"
            data-testid="green-coffee-review-back"
            className="-ml-1 flex h-[30px] w-[30px] items-center justify-center border-0 bg-transparent text-wolisho-brown"
          >
            <BackIcon />
          </button>
          <p className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-black">
            {roasterName ?? 'Green coffee'}
          </p>
          {readOnly && (green.grade || judgePublished) ? (
            <StatusPill green={green} />
          ) : (
            <span
              className="shrink-0 text-xs font-bold tabular-nums text-wolisho-brown/50"
              data-testid="green-coffee-review-position"
            >
              {String(position)} of {String(total)}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className={SHELL}>
          <div className="mb-1.5 flex items-center gap-[7px]">
            <KindBadge green={green} />
          </div>
          <h1 className="mb-4 text-[22px] font-bold leading-tight text-black">{fields.name}</h1>

          {/* One column on a phone, source beside verdict once there is room.
              On a phone the judge's verdict leads, above the source; from lg
              it heads the right column, over the normalized fields. */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:items-start">
            <div data-testid="green-coffee-source-column">
              <SectionHeading label="Source listing" />
              <div className="rounded-xl bg-wolisho-card px-4 py-3.5">
                {showImage && (
                  <div
                    className="mb-3 h-40 w-full overflow-hidden rounded-lg bg-stone-100"
                    data-testid="green-coffee-review-image"
                  >
                    <img
                      src={green.imageUrl ?? undefined}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="h-full w-full object-contain"
                      onError={onImageError}
                    />
                  </div>
                )}
                <SourceLine label="Listing title" value={green.source.title} />
                <SourceLine label="Feed description" value={green.source.feedDescription} />
                <SourceLine label="Product-page description" value={green.source.pageDescription} />
              </div>
            </div>

            {/* From lg: rides along as the source text scrolls; scrolls itself
                if it ever outgrows the viewport. `top-4` matches the scroll
                container's padding, so it sticks without a jump. On a phone it
                is `contents`, so the verdict's `order-first` lifts it above
                the source column. */}
            <div
              data-testid="green-coffee-fields-column"
              className="contents lg:sticky lg:top-4 lg:block lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto"
            >
              {green.judge && (
                <div className="order-first lg:mb-5">
                  <JudgeVerdict judge={green.judge} />
                </div>
              )}
              <div>
                <SectionHeading
                  label="Normalized green coffee"
                  icon={
                    <span className="flex text-green-700">
                      <SproutIcon size={15} />
                    </span>
                  }
                  trailing={
                    editing && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-wolisho-brown px-[7px] py-[3px] text-[10.5px] font-bold uppercase tracking-[0.03em] text-wolisho-cream">
                        <PencilIcon size={10} />
                        Editing
                      </span>
                    )
                  }
                />
                {editing ? (
                  <GreenCoffeeCorrectForm
                    value={editing}
                    original={fields}
                    changed={changed}
                    onChange={(next) => setDraft({ id: green.id, fields: next })}
                  />
                ) : (
                  <div className="rounded-xl bg-wolisho-card px-4 pb-3 pt-1.5">
                    {(
                      [
                        ['name', fields.name],
                        ['countryCode', countryName(fields.countryCode)],
                        ['region', fields.region],
                        ['farm', fields.farm],
                        ['processingType', fields.processingType],
                        ['varieties', fields.varieties],
                        ['tastingNotes', fields.tastingNotes],
                      ] as const
                    ).map(([key, value]) => (
                      <FieldRow
                        key={key}
                        label={FIELD_LABELS[key]}
                        value={value}
                        corrected={correctedKeys.includes(key)}
                      />
                    ))}
                    <div className="flex items-center gap-2.5 pb-1 pt-2.5">
                      <span
                        className={`flex-[0_0_92px] text-[11.5px] font-bold ${
                          correctedKeys.includes('isBlend')
                            ? 'text-wolisho-brown'
                            : 'text-wolisho-brown/70'
                        }`}
                      >
                        Blend
                      </span>
                      <span
                        className={`text-[13px] font-bold ${green.isBlend ? 'text-wolisho-brown' : 'text-black/55'}`}
                      >
                        {green.isBlend ? 'Yes — blend' : 'No — single origin'}
                        {correctedKeys.includes('isBlend') && <CorrectedTag />}
                      </span>
                    </div>
                  </div>
                )}

                {readOnly && reviewerNote && (
                  <div className="mt-4" data-testid="green-coffee-reviewer-note">
                    <p className="mb-1 text-[10.5px] font-bold uppercase tracking-wider text-wolisho-red">
                      Reviewer note
                    </p>
                    <p className="text-[13.5px] italic leading-relaxed text-black/70">
                      &ldquo;{reviewerNote}&rdquo;
                    </p>
                  </div>
                )}
                {readOnly && green.gradedAt && (
                  <p className="mt-4 text-xs font-bold text-wolisho-brown/45">
                    {correctedKeys.length > 0
                      ? `Corrected by an admin ${relTime(green.gradedAt)}`
                      : `Graded ${relTime(green.gradedAt)}`}
                    {green.grade === 'good' && ' · live on the platform'}
                  </p>
                )}
                {judgePublished && (
                  <p
                    className="mt-4 text-xs font-bold text-wolisho-brown/60"
                    data-testid="green-coffee-judge-published-note"
                  >
                    Published by the judge
                    {green.judge ? ` ${relTime(green.judge.judgedAt)}` : ''} · live on the platform
                  </p>
                )}
                {green.judge?.verdict === 'PASS' && green.publishedCoffeeId === null && (
                  <p
                    className="mt-4 text-xs font-bold text-wolisho-brown/60"
                    data-testid="green-coffee-judge-publishing-note"
                  >
                    The judge passed it · publishing, no grade needed
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showActions && (
        <div className="shrink-0 border-t border-wolisho-brown/10 px-4 py-3">
          {editing ? (
            <div className={SHELL}>
              <p
                className="mb-2 text-[12.5px] font-bold text-wolisho-brown/70"
                data-testid="green-coffee-correct-summary"
              >
                {changed.length === 0
                  ? 'Change a field to publish a correction'
                  : `${pluralFields(changed.length)} · ${fieldLabels(changed)}`}
                {correctionBlocker && ` · ${correctionBlocker}`}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={exitEdit}
                  data-testid="green-coffee-correct-cancel"
                  className="flex-1 rounded-lg border border-wolisho-brown/30 bg-transparent px-3 py-3 text-sm font-bold text-wolisho-brown"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSaving || changed.length === 0 || correctionBlocker !== null}
                  onClick={() => {
                    if (corrected) onCorrect(green.id, corrected);
                  }}
                  data-testid="green-coffee-correct-publish"
                  className="inline-flex flex-[1.7] items-center justify-center gap-1.5 rounded-lg border-0 bg-wolisho-red px-3 py-3 text-sm font-bold text-white disabled:opacity-60"
                >
                  <CheckIcon size={16} />
                  Publish corrected
                </button>
              </div>
            </div>
          ) : noteMode ? (
            <div className={SHELL}>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  noteMode === 'bad'
                    ? 'Why is this bad? (optional) — feeds prompt & config fixes'
                    : 'Why is this good? (optional) — feeds prompt & config fixes'
                }
                data-testid={`green-coffee-${noteMode}-note`}
                className="min-h-[62px] w-full resize-none rounded-lg border border-wolisho-brown/20 bg-white px-3 py-2.5 text-[13.5px] leading-snug text-black placeholder:text-black/30 focus:outline-hidden"
              />
              <div className="mt-2.5 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setNoteMode(null);
                    setNote('');
                  }}
                  className="flex-1 rounded-lg border border-wolisho-brown/25 bg-transparent px-3 py-2.5 text-[13.5px] font-bold text-wolisho-brown"
                >
                  Cancel
                </button>
                {noteMode === 'bad' ? (
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => onBad(green.id, note)}
                    data-testid="green-coffee-bad-confirm"
                    className="inline-flex flex-[1.4] items-center justify-center gap-1.5 rounded-lg border-0 bg-wolisho-brown px-3 py-2.5 text-[13.5px] font-bold text-white disabled:opacity-60"
                  >
                    <CrossIcon size={14} />
                    Mark bad
                  </button>
                ) : (
                  <PublishButton
                    compact
                    isUpdate={green.isUpdate}
                    disabled={isSaving || !canPublish}
                    onClick={() => onGood(green.id, note)}
                    testId="green-coffee-good-confirm"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className={SHELL}>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNoteMode('bad')}
                  data-testid="green-coffee-bad"
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-wolisho-brown/30 bg-transparent px-3 py-3 text-sm font-bold text-wolisho-brown"
                >
                  <CrossIcon />
                  Bad
                </button>
                <button
                  type="button"
                  onClick={() => setDraft({ id: green.id, fields })}
                  data-testid="green-coffee-correct"
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-wolisho-brown bg-wolisho-brown/[0.06] px-3 py-3 text-sm font-bold text-wolisho-brown"
                >
                  <PencilIcon />
                  Correct
                </button>
                <PublishButton
                  isUpdate={green.isUpdate}
                  disabled={isSaving || !canPublish}
                  onClick={() => onGood(green.id)}
                  testId="green-coffee-good"
                />
              </div>
              {canPublish ? (
                <div className="mt-1.5 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setNoteMode('good')}
                    data-testid="green-coffee-good-with-note"
                    className="border-0 bg-transparent px-1 py-1 text-[12.5px] font-bold text-wolisho-brown/60 underline-offset-2 hover:underline"
                  >
                    Good with note
                  </button>
                </div>
              ) : (
                <p
                  className="mt-2 text-[13px] font-bold text-wolisho-brown/60"
                  data-testid="green-coffee-no-country-note"
                >
                  No country — can&apos;t publish. Correct it to set one, or mark bad.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
